import React, { useEffect, useRef, useState } from 'react'
import { Box, Flex, Text, useDisclosure } from '@chakra-ui/core'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/core'
import { Bell as BellIcon } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { ActionButton } from 'components/Sidebar/NavMenu'
import { NotificationCenterPanel } from 'components/Notifications/NotificationCenterPanel'
import { NotificationDesktopModal } from 'components/Notifications/desktop/NotificationDesktopModal'
import { handleFetchUnreadCount } from 'redux/actions/notifications'
import { unreadCountSelector } from 'redux/selectors/notifications'
import { notifTokens, NOTIF_DESKTOP_BREAKPOINT } from 'services/notifications/designTokens'
import useWindowDimensions from 'hooks/useWindowDimensions'
import LocaleText from 'components/LocaleText'

// Adapted from https://dribbble.com/shots/2604805-Notification-Icon-Animation — same idea (bell
// swings, badge pops in) but keeps our actual lucide glyph instead of rebuilding a custom
// hand-drawn bell shape, so it stays visually consistent with every other sidebar icon.
//
// The swing itself is the first 20% of one 3.6s cycle, looped forever (pure CSS, no JS timers)
// while there's at least one unread notification — the rest of the cycle just holds at rest so it
// reads as "ring, pause, ring" instead of a nonstop wobble.
const ring = keyframes`
  0%, 100% { transform: rotate(0deg); }
  2% { transform: rotate(16deg); }
  4% { transform: rotate(-14deg); }
  6.4% { transform: rotate(10deg); }
  8.8% { transform: rotate(-8deg); }
  11.2% { transform: rotate(6deg); }
  13.6% { transform: rotate(-4deg); }
  16% { transform: rotate(2deg); }
  18% { transform: rotate(-1deg); }
  20% { transform: rotate(0deg); }
`

const popIn = keyframes`
  0% { transform: scale(0.4); opacity: 0; }
  65% { transform: scale(1.18); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`

const RingingIconTile = styled(Flex)<{ hasUnread: boolean }>`
  transform-origin: 50% 0%;
  animation: ${({ hasUnread }) => (hasUnread ? ring : 'none')} 3.6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const PoppingBadge = styled(Flex)`
  inset-inline-end: -6px;
  animation: ${popIn} 320ms cubic-bezier(0.34, 1.56, 0.64, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

interface IBellTileProps {
  isActive: boolean
  unreadCount: number
  // Whether to run the ring loop — false while the notification modal/panel is already open
  // (the user is already looking at their notifications, nothing to draw attention to).
  shouldRing: boolean
  badgeAnimKey: number
  onClick: () => void
}

const BellTile = React.forwardRef<any, IBellTileProps>(
  ({ isActive, unreadCount, shouldRing, badgeAnimKey, onClick }, ref) => (
    <ActionButton
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick()
        }
      }}
    >
      <Box position="relative" flexShrink={0}>
        <RingingIconTile
          hasUnread={shouldRing}
          w="var(--sidebar-icon-size, 38px)"
          minW="var(--sidebar-icon-size, 38px)"
          h="var(--sidebar-icon-size, 38px)"
          p="0"
          alignItems="center"
          justifyContent="center"
        >
          <BellIcon size={28} strokeWidth={1.75} color={notifTokens.ink} />
        </RingingIconTile>
        {unreadCount > 0 && (
          <PoppingBadge
            key={badgeAnimKey}
            position="absolute"
            top="-6px"
            minW="18px"
            h="16px"
            px="4px"
            borderRadius="999px"
            bg={notifTokens.badge}
            color="white"
            alignItems="center"
            justifyContent="center"
            fontSize="10px"
            fontWeight={600}
            border="2px solid white"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </PoppingBadge>
        )}
      </Box>
      <Text fontWeight="700">
        <LocaleText text="mobile_menu_notifications" />
      </Text>
    </ActionButton>
  )
)

BellTile.displayName = 'BellTile'

// Above NOTIF_DESKTOP_BREAKPOINT this opens the 3-pane desktop modal (spec §2); below it, a
// popover anchored to the bell (spec §3). Same bell, same disclosure state — only the surface differs.
//
// `setIsHidden` is only passed when this button is rendered inside the mobile burger-menu drawer
// (see NavMenu.tsx) — same pattern as ComposeButton/CreateTaskV2Button/ChooseContextButton: hide
// the drawer (not close it — MobileMenu.tsx toggles its own `display`) while this button's own
// overlay is open, otherwise the drawer stays on top of the notification popover/modal.
export const NotificationBellButton: React.FC<{ setIsHidden?: (isHidden: boolean) => void }> = ({ setIsHidden }) => {
  const dispatch = useDispatch()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const unreadCount = useSelector(unreadCountSelector)
  const { windowWidth } = useWindowDimensions()
  const isDesktop = windowWidth >= NOTIF_DESKTOP_BREAKPOINT

  const handleOpen = () => {
    setIsHidden?.(true)
    onOpen()
  }

  const handleClose = () => {
    setIsHidden?.(false)
    onClose()
  }

  useEffect(() => {
    dispatch(handleFetchUnreadCount())

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') dispatch(handleFetchUnreadCount())
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The ring itself is a pure-CSS loop keyed off `shouldRing` (see RingingIconTile) — no JS needed
  // for that. This only tracks increases to replay the badge's pop-in — not on the very
  // first load (there could already be unread notifications sitting there, that's not "new"), and
  // not when it goes down (marking things read shouldn't animate). `prevUnreadCountRef` starts at
  // `null` so the first effect run just baselines it silently instead of popping for pre-existing unread.
  const prevUnreadCountRef = useRef<number | null>(null)
  const [badgeAnimKey, setBadgeAnimKey] = useState(0)

  useEffect(() => {
    const prev = prevUnreadCountRef.current
    prevUnreadCountRef.current = unreadCount
    if (prev === null || unreadCount <= prev) return
    setBadgeAnimKey((key) => key + 1)
  }, [unreadCount])

  const handleToggle = () => (isOpen ? handleClose() : handleOpen())
  // Don't ring while the user already has the notification modal/panel open — nothing to draw
  // their attention to, they're already looking at it.
  const shouldRing = unreadCount > 0 && !isOpen

  if (isDesktop) {
    return (
      <>
        <BellTile
          isActive={isOpen}
          unreadCount={unreadCount}
          shouldRing={shouldRing}
          badgeAnimKey={badgeAnimKey}
          onClick={handleToggle}
        />
        <NotificationDesktopModal isOpen={isOpen} onClose={handleClose} />
      </>
    )
  }

  // Not a Chakra Popover: on tablet/mobile this bell can render inside the burger-menu drawer,
  // which is display:none'd while open (see handleOpen/handleClose above) — a Popper-anchored
  // popover would have no visible trigger left to position against. NotificationCenterPanel is a
  // self-contained fixed-position portal instead (own scrim, own positioning), immune to that.
  return (
    <>
      <BellTile
        isActive={isOpen}
        unreadCount={unreadCount}
        shouldRing={shouldRing}
        badgeAnimKey={badgeAnimKey}
        onClick={handleToggle}
      />
      <NotificationCenterPanel isOpen={isOpen} onClose={handleClose} />
    </>
  )
}
